import { useCallback, useEffect, useState } from "react";

import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { PriceRange, Product } from "~/types/product";

export const useProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [priceRanges, setPriceRanges] = useState<PriceRange[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);

      // Query the products table directly to ensure we get all columns including weight_kg and volume_cm3
      const { data: productsData, error: productsError } = await supabase
        .from("products")
        .select("*")
        .is("deleted_at", null);

      if (productsError) throw productsError;

      // Fetch price ranges
      const { data: priceRangesData, error: priceRangesError } = await supabase
        .from("price_ranges")
        .select("*");

      if (priceRangesError) throw priceRangesError;

      // Map price ranges to products. Keep `products.stock` as-is — it's the físico
      // (bodega) single source of truth. The old wms_inventory box_count override was
      // stale: the WMS position/ingestion workflow was abandoned (see gotchas
      // fg-stock-goforward-seeding-gap), so it showed wrong numbers (e.g. 03010007 583 vs 112).
      const productsWithPriceRanges =
        productsData?.map((product) => {
          const productPriceRanges =
            priceRangesData?.filter(
              (range) => range.product_id === product.id
            ) || [];
          return {
            ...product,
            price_ranges: productPriceRanges,
          };
        }) || [];

      setProducts(productsWithPriceRanges || []);
      setPriceRanges(priceRangesData || []);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError(
        err instanceof Error ? err.message : "Unknown error fetching products"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Function to soft delete a product
  const deleteProduct = useCallback(
    async (productId: string) => {
      try {
        // Since we have a trigger set up, we can simply call delete
        // The trigger will update the deleted_at field instead of actually deleting
        const { error } = await supabase
          .from("products")
          .delete()
          .eq("id", productId);

        if (error) throw error;

        // Refresh the products list
        await fetchProducts();

        toast({
          title: "Producto eliminado",
          description: "El producto ha sido eliminado correctamente.",
        });

        return true;
      } catch (err) {
        console.error("Error deleting product:", err);
        toast({
          title: "Error al eliminar producto",
          description:
            "No se pudo eliminar el producto. Por favor, intente de nuevo.",
          variant: "destructive",
        });
        return false;
      }
    },
    [fetchProducts, toast]
  );

  // Add a new product or update an existing one
  const saveProduct = useCallback(
    async (productData: any, isNew: boolean = false) => {
      try {
        setSaving(true);
        // Separate price_ranges from the product data
        const { price_ranges, id, ...productToSave } = productData;

        // Blank barcode must be NULL, not "" — the partial unique index
        // products_barcode_unique_active (WHERE deleted_at IS NULL AND barcode IS NOT NULL)
        // treats "" as a real value, so a 2nd blank-barcode product collides (23505).
        if (
          typeof productToSave.barcode === "string" &&
          productToSave.barcode.trim() === ""
        ) {
          productToSave.barcode = null;
        }

        // Capitalize product names and descriptions
        if (productToSave.short_name) {
          productToSave.short_name = productToSave.short_name.toUpperCase();
        }
        if (productToSave.long_name) {
          productToSave.long_name = productToSave.long_name.toUpperCase();
        }
        if (productToSave.description) {
          productToSave.description = productToSave.description.toUpperCase();
        }

        // Validate price ranges
        if (!price_ranges || price_ranges.length === 0) {
          throw new Error("Al menos un rango de precios es requerido");
        }

        // Validate and clean price ranges data
        const validatedPriceRanges = price_ranges.map((range: PriceRange) => ({
          min: range.min < 0 ? 0 : (range.min ?? 0), // Allow 0 as minimum (inclusive range)
          max: range.max || 100, // Default to 100 but allow equal to min
          price: Math.max(0, range.price || 0),
        }));

        // Sort price ranges by min value to ensure consistency
        validatedPriceRanges.sort((a, b) => a.min - b.min);

        if (isNew) {
          // Add new product - do NOT include ID so Supabase will generate one
          const { data, error } = await supabase
            .from("products")
            .insert(productToSave)
            .select();

          if (error) throw error;

          if (data && data.length > 0) {
            // Associate price ranges with the new product ID
            const priceRangesToInsert = validatedPriceRanges.map((range) => ({
              product_id: data[0].id,
              min: range.min,
              max: range.max,
              price: range.price,
            }));

            const { error: priceRangeError } = await supabase
              .from("price_ranges")
              .insert(priceRangesToInsert);

            if (priceRangeError) throw priceRangeError;
          }

          toast({
            title: "Producto creado",
            description: "El producto ha sido creado correctamente.",
          });

          await fetchProducts();
          return data?.[0] || null;
        } else {
          // For updates, we do need the ID
          if (!id) {
            throw new Error("ID is required for updating a product");
          }

          // Update existing product
          const { data, error } = await supabase
            .from("products")
            .update(productToSave)
            .eq("id", id)
            .select();

          if (error) throw error;

          if (data && data.length > 0) {
            // First delete existing price ranges for this product
            const { error: deleteError } = await supabase
              .from("price_ranges")
              .delete()
              .eq("product_id", id);

            if (deleteError) throw deleteError;

            // Then insert the new price ranges
            const priceRangesToInsert = validatedPriceRanges.map((range) => ({
              product_id: id,
              min: range.min,
              max: range.max,
              price: range.price,
            }));

            const { error: priceRangeError } = await supabase
              .from("price_ranges")
              .insert(priceRangesToInsert);

            if (priceRangeError) throw priceRangeError;
          }

          toast({
            title: "Producto actualizado",
            description: "El producto ha sido actualizado correctamente.",
          });

          await fetchProducts();
          return data?.[0] || null;
        }
      } catch (err) {
        console.error("Error saving product:", err);
        // Supabase throws a PostgrestError (a plain object, not an Error), so read
        // its fields directly and map the barcode collision to a clear message.
        const dbErr = err as { code?: string; message?: string } | null;
        const description =
          dbErr?.code === "23505" && dbErr?.message?.includes("barcode")
            ? "Ya existe un producto activo con ese código de barras."
            : (dbErr?.message ??
              "No se pudo guardar el producto. Por favor, intente de nuevo.");
        toast({
          title: "Error al guardar producto",
          description,
          variant: "destructive",
        });
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchProducts, toast]
  );

  // Import products from CSV data
  const importProducts = useCallback(
    async (productsData: Partial<Product>[], updateMode: boolean = false) => {
      try {
        // Capitalize product names in imported data
        const capitalizedProductsData = productsData.map((product) => ({
          ...product,
          short_name: product.short_name
            ? product.short_name.toUpperCase()
            : product.short_name,
          long_name: product.long_name
            ? product.long_name.toUpperCase()
            : product.long_name,
          description: product.description
            ? product.description.toUpperCase()
            : product.description,
          // Blank barcode must be NULL, not "" — see products_barcode_unique_active.
          barcode:
            typeof product.barcode === "string" && product.barcode.trim() === ""
              ? null
              : product.barcode,
        }));

        if (updateMode) {
          // Update mode - process products with IDs or barcodes for updates
          let updatedCount = 0;
          let createdCount = 0;
          let skippedCount = 0;
          const errors = [];

          // Get existing products to check for barcode conflicts and matches
          const { data: existingProducts, error: fetchError } = await supabase
            .from("products")
            .select("id, short_name, barcode, sku")
            .is("deleted_at", null);

          if (fetchError) throw fetchError;

          // Create maps for conflict detection and matching
          const existingByBarcode = new Map();
          const existingById = new Map();

          existingProducts?.forEach((product) => {
            existingById.set(product.id, product);
            if (product.barcode) {
              existingByBarcode.set(product.barcode, product);
            }
          });

          for (const current of capitalizedProductsData) {
            try {
              if (current.id) {
                // Method 1: Update by ID (existing behavior)
                const existingProduct = existingById.get(current.id);
                const updateData = { ...current };
                delete updateData.id; // Remove ID from update data

                // Handle barcode conflicts
                if (current.barcode && existingProduct) {
                  const conflictProduct = existingByBarcode.get(
                    current.barcode
                  );

                  if (conflictProduct && conflictProduct.id !== current.id) {
                    console.warn(
                      `🔄 Barcode conflict for ${current.short_name}: ${current.barcode} exists in ${conflictProduct.short_name}`
                    );

                    // Clear the conflicting barcode from the other product first
                    await supabase
                      .from("products")
                      .update({ barcode: null })
                      .eq("id", conflictProduct.id);

                    console.log(
                      `✅ Cleared conflicting barcode from ${conflictProduct.short_name}`
                    );
                  }
                }

                // Now update the current product
                const { data: updateResult, error: updateError } =
                  await supabase
                    .from("products")
                    .update(updateData)
                    .eq("id", current.id)
                    .select();

                if (updateError) {
                  console.error(
                    `Error updating product ${current.id}:`,
                    updateError
                  );
                  errors.push(`${current.short_name}: ${updateError.message}`);
                  skippedCount++;
                } else {
                  updatedCount++;

                  // Update our local maps
                  if (current.barcode) {
                    existingByBarcode.set(current.barcode, {
                      id: current.id,
                      short_name: current.short_name,
                      barcode: current.barcode,
                    });
                  }
                }
              } else if (
                current.barcode &&
                existingByBarcode.has(current.barcode)
              ) {
                // Method 2: Update by barcode (new behavior)
                const existingProduct = existingByBarcode.get(current.barcode);
                const updateData = { ...current };
                delete updateData.id; // Remove any potential ID

                const { data: updateResult, error: updateError } =
                  await supabase
                    .from("products")
                    .update(updateData)
                    .eq("id", existingProduct.id)
                    .select();

                if (updateError) {
                  console.error(
                    `Error updating product ${existingProduct.id} by barcode:`,
                    updateError
                  );
                  errors.push(`${current.short_name}: ${updateError.message}`);
                  skippedCount++;
                } else {
                  updatedCount++;

                  // Update our local maps
                  existingByBarcode.set(current.barcode, {
                    id: existingProduct.id,
                    short_name: current.short_name,
                    barcode: current.barcode,
                  });
                }
              } else {
                // Method 3: Create new product (no ID or barcode match)
                console.log(`➕ Creating new product: ${current.short_name}`);

                const { error: insertError } = await supabase
                  .from("products")
                  .insert(current);

                if (insertError) {
                  console.error(`Error creating product:`, insertError);
                  errors.push(`${current.short_name}: ${insertError.message}`);
                  skippedCount++;
                } else {
                  console.log(`✅ Created new product: ${current.short_name}`);
                  createdCount++;
                }
              }
            } catch (error) {
              console.error(
                `Error processing product ${current.short_name}:`,
                error
              );
              errors.push(
                `${current.short_name}: ${error instanceof Error ? error.message : "Error desconocido"}`
              );
              skippedCount++;
            }
          }

          let message = `Procesamiento completado: ${updatedCount} productos actualizados`;
          if (createdCount > 0) {
            message += `, ${createdCount} productos creados`;
          }
          if (skippedCount > 0) {
            message += `, ${skippedCount} productos omitidos por errores`;
          }

          // Show detailed errors if any
          if (errors.length > 0) {
            console.error("🚨 ERRORES DETALLADOS:");
            errors.forEach((error) => console.error(`- ${error}`));
          }

          toast({
            title:
              skippedCount > 0
                ? "Actualización completada con advertencias"
                : "Actualización completada",
            description:
              errors.length > 0
                ? `${message}\n\nVea la consola para detalles de errores.`
                : message,
            variant: skippedCount > 0 ? "destructive" : "default",
          });
        } else {
          // Create mode - original logic for creating new products
          // Get existing products to check for duplicates by name
          const { data: existingProducts, error: fetchError } = await supabase
            .from("products")
            .select("id, short_name, barcode")
            .is("deleted_at", null);

          if (fetchError) throw fetchError;

          // Create a map of existing products by name and barcode
          const existingByName = new Map();
          const existingByBarcode = new Map();

          existingProducts?.forEach((product) => {
            existingByName.set(product.short_name, product);
            if (product.barcode) {
              existingByBarcode.set(product.barcode, product);
            }
          });

          // Process import data to handle duplicates intelligently
          const processedData = [];
          const skippedDuplicates = [];

          for (const current of capitalizedProductsData) {
            let shouldImport = true;
            let existingProduct = null;

            // Check for duplicates by barcode first (if barcode exists)
            if (current.barcode && existingByBarcode.has(current.barcode)) {
              existingProduct = existingByBarcode.get(current.barcode);
              shouldImport = false;
            }
            // Check for duplicates by name
            else if (
              current.short_name &&
              existingByName.has(current.short_name)
            ) {
              existingProduct = existingByName.get(current.short_name);

              // If existing product has no barcode but import data has barcode, update existing
              if (!existingProduct.barcode && current.barcode) {
                // Update existing product with barcode and other new data
                const updateData = { ...current };
                delete updateData.id; // Remove ID to avoid conflicts

                const { error: updateError } = await supabase
                  .from("products")
                  .update(updateData)
                  .eq("id", existingProduct.id);

                if (updateError) {
                  console.error(
                    `Error updating product ${existingProduct.id}:`,
                    updateError
                  );
                } else {
                  console.log(
                    `Updated existing product: ${current.short_name}`
                  );
                }
                shouldImport = false;
              }
              // If both have barcodes or both don't have barcodes, skip import
              else {
                shouldImport = false;
              }
            }

            if (shouldImport) {
              processedData.push(current);
            } else {
              skippedDuplicates.push({
                name: current.short_name,
                reason: existingProduct?.barcode
                  ? "duplicate barcode"
                  : "duplicate name",
              });
            }
          }

          console.log(
            `Procesando ${capitalizedProductsData.length} productos: ${processedData.length} nuevos, ${skippedDuplicates.length} duplicados omitidos`
          );

          if (skippedDuplicates.length > 0) {
            console.log("Productos duplicados omitidos:", skippedDuplicates);
          }

          let importedCount = 0;

          if (processedData.length > 0) {
            const { data, error } = await supabase
              .from("products")
              .insert(processedData)
              .select();

            if (error) throw error;
            importedCount = data?.length || 0;
          }

          let message =
            processedData.length > 0
              ? `Se han importado ${importedCount} productos nuevos correctamente.`
              : "No se encontraron productos nuevos para importar.";

          if (skippedDuplicates.length > 0) {
            message += ` ${skippedDuplicates.length} productos duplicados fueron omitidos.`;
          }

          toast({
            title: "Importación completada",
            description: message,
          });
        }

        await fetchProducts();
        return capitalizedProductsData || [];
      } catch (err) {
        console.error("Error importing products:", err);
        toast({
          title: "Error al importar productos",
          description:
            "No se pudieron importar los productos. Por favor, intente de nuevo.",
          variant: "destructive",
        });
        return [];
      }
    },
    [fetchProducts, toast]
  );

  // Function to restore a soft-deleted product
  const restoreProduct = useCallback(
    async (productId: string) => {
      try {
        // Call the restore_product function we created in the migration
        const { data, error } = await supabase.rpc("restore_product", {
          product_id: productId,
        });

        if (error) throw error;

        // Refresh the products list
        await fetchProducts();

        toast({
          title: "Producto restaurado",
          description: "El producto ha sido restaurado correctamente.",
        });

        return true;
      } catch (err) {
        console.error("Error restoring product:", err);
        toast({
          title: "Error al restaurar producto",
          description:
            "No se pudo restaurar el producto. Por favor, intente de nuevo.",
          variant: "destructive",
        });
        return false;
      }
    },
    [fetchProducts, toast]
  );

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return {
    products,
    priceRanges,
    loading,
    saving,
    error,
    fetchProducts,
    deleteProduct,
    saveProduct,
    importProducts,
    restoreProduct,
  };
};
